/**
 * Shared MFA enrollment return paths (client + server safe).
 * After Keycloak AIA (`kc_action=CONFIGURE_TOTP`), OAuth callback sends the user here.
 */

/** Fallback when returnTo is missing or not a same-origin relative path. */
export const MFA_ENROLL_RETURN_FALLBACK =
  "/u/dashboard?settingsOpen=1&settingsTab=security";

/** Query marker so the OAuth callback can toast “2FA enabled” instead of “Already Connected”. */
const MFA_ENROLL_QUERY = "mfaEnroll";

/**
 * Ensure a relative in-app path for post-enroll redirect.
 * Rejects protocol-relative / absolute URLs; falls back to dashboard + settings.
 */
export function resolveMfaEnrollReturnTo(returnTo?: string | null): string {
  const trimmed = returnTo?.trim() ?? "";
  if (!trimmed.startsWith("/") || trimmed.startsWith("//")) {
    return MFA_ENROLL_RETURN_FALLBACK;
  }
  return trimmed;
}

/** Strip the transient enroll marker before the final in-app redirect. */
export function stripMfaEnrollQuery(pathWithSearch: string): string {
  try {
    const url = new URL(pathWithSearch, "http://local.invalid");
    url.searchParams.delete(MFA_ENROLL_QUERY);
    return `${url.pathname}${url.search}`;
  } catch {
    return pathWithSearch;
  }
}

export function withMfaEnrollQuery(pathWithSearch: string): string {
  const url = new URL(pathWithSearch, "http://local.invalid");
  url.searchParams.set(MFA_ENROLL_QUERY, "1");
  if (!url.searchParams.has("settingsOpen")) {
    url.searchParams.set("settingsOpen", "1");
  }
  if (!url.searchParams.has("settingsTab")) {
    url.searchParams.set("settingsTab", "security");
  }
  return `${url.pathname}${url.search}`;
}

export function isMfaEnrollReturnTo(
  pathWithSearch: string | null | undefined,
): boolean {
  if (!pathWithSearch) return false;
  try {
    const url = new URL(pathWithSearch, "http://local.invalid");
    return url.searchParams.get(MFA_ENROLL_QUERY) === "1";
  } catch {
    return false;
  }
}
